<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.dashboard'),
                        href: meta.links.dashboard_index,
                    },
                    {
                        name: $tc('messages.domains', 2),
                        href: meta.links.index,
                    },
                    {
                        name: category.name,
                        href: null,
                    },
                ]"
            />
        </template>

        <TitleFieldsFormSection>
            <template v-slot:title>
                <div>{{ $t('messages.information') }}</div>
                <div class="flex gap-x-1 items-center">
                    <IconBrandYoutube class="text-red-500 h-6 w-6" />
                    <a
                        target="_blank"
                        class="text-indigo-500"
                        href="https://www.youtube.com/watch?v=OSQtK_Pb63c&list=PLZZYS-CAqB3cEbkwh9EM6ejT9xsIoiqKK&index=2"
                    >
                        {{ $tc('messages.tutorials', 1) }}
                    </a>
                </div>
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.stages', 1)" for="stage_id" />
                        {{ category.stage_name }}
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.name')" for="name" />
                        {{ category.name }}
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $tc('messages.subject', 2) }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12" v-for="subject in category.subjects" :key="subject.id">
                    <div class="flex space-x-4">
                        <div>
                            {{ subject.name }}
                        </div>

                        <div class="flex space-x-2">
                            <SubjectForm
                                is-edit
                                :subject="subject"
                                :endpoint="subject.subject_update_endpoint"
                                v-if="subject.can.edit"
                            />
                            <InertiaSquareConfirmActionButton
                                :confirm-text="$t('messages.delete')"
                                :href="subject.links.delete"
                                danger
                                method="delete"
                                v-if="subject.can.delete"
                            >
                                <template v-slot:icon>
                                    <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                        <path
                                            fill-rule="evenodd"
                                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                </template>
                            </InertiaSquareConfirmActionButton>
                        </div>
                    </div>
                </div>

                <SubjectForm
                    ref="createSubjectForm"
                    :desired-action="category.subjects.length === 0"
                    :endpoint="category.subject_endpoint"
                    :info="meta.info.without_subjects_prompt"
                />
            </div>
        </TitleFieldsFormSection>

        <div v-if="category.dimensions.length === 0">
            <AlertMessage class="mt-5 inline-block">
                <template v-slot:title>
                    {{ $t('messages.warning') }}
                </template>
                <template v-slot:body> {{ $t('messages.no_dimensions_in_domain_warning') }} </template>
            </AlertMessage>
        </div>

        <PageSectionCollapsible>
            <template v-slot:title>
                {{ $tc('messages.dimensions', 2) }}
            </template>

            <div class="mt-5">
                <div class="bg-gray-100 overflow-hidden rounded-lg mt-3">
                    <div class="px-4 py-5 sm:p-6">
                        <div class="flex flex-col mt-5" v-if="category.dimensions.length > 0">
                            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                                <div
                                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                                >
                                    <table class="min-w-full divide-y divide-gray-200">
                                        <template
                                            v-for="dimension in category.dimensions"
                                            :key="`${dimension.id}_${dimension.updated_at}`"
                                        >
                                            <thead>
                                                <tr>
                                                    <th
                                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider"
                                                    >
                                                        {{ dimension.abbreviation }} -
                                                        {{ dimension.title }}
                                                    </th>
                                                    <th
                                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                                    >
                                                        <div class="flex justify-end space-x-2">
                                                            <DimensionForm
                                                                is-edit
                                                                :dimension="dimension"
                                                                :endpoint="dimension.dimension_update_endpoint"
                                                                v-if="dimension.can.edit"
                                                            />
                                                            <InertiaSquareDeleteButton
                                                                :href="dimension.links.delete"
                                                                :confirm-text="$t('messages.are_you_sure?')"
                                                                v-if="dimension.can.delete"
                                                            />
                                                        </div>
                                                    </th>
                                                </tr>
                                                <tr>
                                                    <th
                                                        id="description"
                                                        colspan="2"
                                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider"
                                                        v-if="dimension.description"
                                                    >
                                                        <span class="whitespace-pre-line">{{
                                                            dimension.description
                                                        }}</span>
                                                    </th>
                                                </tr>
                                                <tr
                                                    :class="'bg-white'"
                                                    v-for="competence in dimension.competences"
                                                    :key="`${competence.id}_${competence.updated_at}`"
                                                >
                                                    <td class="px-6 py-4 text-sm leading-5 text-gray-500">
                                                        {{ competence.description }}
                                                    </td>
                                                    <td
                                                        class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500"
                                                    >
                                                        <div class="flex justify-end space-x-2">
                                                            <CompetenceForm
                                                                is-edit
                                                                :competence="competence"
                                                                :endpoint="competence.competence_update_endpoint"
                                                            />
                                                            <InertiaSquareDeleteButton
                                                                :href="competence.links.delete"
                                                                :confirm-text="$t('messages.are_you_sure?')"
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            </thead>
                                        </template>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="px-4 py-5 sm:p-6">
                        <DimensionForm
                            :endpoint="category.dimension_endpoint"
                            :desired-action="category.dimensions.length === 0"
                            :info="meta.info.dimensions_explanation"
                        />
                    </div>
                </div>
            </div>
        </PageSectionCollapsible>

        <div v-for="subject in category.subjects" :key="subject.id">
            <div class="font-bold">
                {{ subject.name }}
            </div>
            <div v-if="meta.can.edit">
                <SubjectExpectedLevelsEditor :subject="subject" />
            </div>
            <div v-if="subject.contentBlocks">
                <SubjectContentBlocksEditor :subject="subject" :can-edit="meta.can.edit" />
            </div>
        </div>
    </PanelPage>
</template>

<script>
import AdminLayout from '@/Shared/AdminLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import DimensionForm from '@/components/pageSpecific/DimensionForm.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import CompetenceForm from '@/components/pageSpecific/CompetenceForm.vue';
import SubjectForm from '@/components/pageSpecific/SubjectForm.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import { IconBrandYoutube } from '@tabler/icons-vue';
import SubjectContentBlocksEditor from '@/Pages/Admin/Categories/partials/SubjectContentBlocksEditor.vue';
import SubjectExpectedLevelsEditor from '@/Pages/Admin/Categories/partials/SubjectExpectedLevelsEditor.vue';
import PageSectionCollapsible from '../../../Shared/PageSectionCollapsible.vue';

export default {
    components: {
        PageSectionCollapsible,
        SubjectExpectedLevelsEditor,
        SubjectContentBlocksEditor,
        AlertMessage,
        InertiaSquareDeleteButton,
        PanelPage,
        TitleFieldsFormSection,
        FormLabel,
        DimensionForm,
        CompetenceForm,
        SubjectForm,
        InertiaSquareConfirmActionButton,
        IconBrandYoutube,
    },
    layout: AdminLayout,
    name: 'Show',
    props: ['teacher', 'meta', 'options', 'category'],
    data() {
        return {};
    },
    mounted() {
        if (this.category.subjects.length === 0) {
            this.$refs.createSubjectForm.open(this.category.name);
        }
    },
};
</script>
